import { useCallback, useRef, useState, type CSSProperties, type RefObject } from "react";
import { Popover } from "@/components/common/Popover";
import { CHAT_EFFORTS, type ChatEffort } from "@/lib/chat";

const EFFORT_LABELS: Record<ChatEffort, string> = {
	low: "Low",
	medium: "Medium",
	high: "High",
	xhigh: "Extra High",
	max: "Max",
};

const POPOVER_WIDTH = 212;
const TRACK_SIZE = 24;
const THUMB_SIZE = 28;
const DOT_SIZE = 4;
const PARTICLE_COUNT = 14;

// 粒子の並びは開くたびに同じでよいので、読み込み時に一度だけ決める。
const PARTICLES = Array.from({ length: PARTICLE_COUNT }, (_, id) => {
	const duration = 1.2 + Math.random() * 1.2;
	return {
		id,
		top: 15 + Math.random() * 70,
		size: 1.5 + Math.random() * 1.5,
		opacity: 0.35 + Math.random() * 0.55,
		duration,
		delay: -Math.random() * duration,
	};
});

interface ChatEffortSelectorProps {
	supportedEfforts: readonly ChatEffort[];
	value: ChatEffort | null;
	onChange: (effort: ChatEffort) => void;
	disabled: boolean;
}

/** 推論レベルを段階スライダーで選ぶ、入力欄のツールバー用ボタン。 */
export function ChatEffortSelector({
	supportedEfforts,
	value,
	onChange,
	disabled,
}: ChatEffortSelectorProps) {
	const [open, setOpen] = useState(false);
	// 無効になったら閉じておき、有効に戻ったときに勝手に開き直らないようにする。
	if (disabled && open) setOpen(false);
	const close = useCallback(() => setOpen(false), []);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const sliderRef = useRef<HTMLInputElement>(null);
	const efforts = CHAT_EFFORTS.filter((effort) => supportedEfforts.includes(effort));
	const label = value === null ? "未選択" : EFFORT_LABELS[value];

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				disabled={disabled}
				onClick={() => setOpen((current) => !current)}
				data-testid="chat-effort-selector"
				className="izanagi-dropdown-trigger"
				data-size="compact"
			>
				<span data-testid="chat-effort-selector-label">{label}</span>
			</button>
			{open && (
				<Popover
					anchorRef={triggerRef}
					onClose={close}
					anchored
					align="right"
					initialFocusRef={sliderRef}
					width={POPOVER_WIDTH}
					padding={14}
					testId="chat-effort-selector-popover"
				>
					<EffortSlider
						efforts={efforts}
						index={value === null ? -1 : efforts.indexOf(value)}
						label={label}
						onChange={onChange}
						sliderRef={sliderRef}
					/>
				</Popover>
			)}
		</>
	);
}

function EffortSlider({
	efforts,
	index,
	label,
	onChange,
	sliderRef,
}: {
	efforts: ChatEffort[];
	index: number;
	label: string;
	onChange: (effort: ChatEffort) => void;
	sliderRef: RefObject<HTMLInputElement | null>;
}) {
	const max = efforts.length - 1;
	const position = Math.max(index, 0);
	const ratio = max === 0 ? 1 : position / max;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
			<div style={{ display: "flex", alignItems: "baseline" }}>
				<span className="text-caption" style={{ color: "var(--fg-2)" }}>
					推論レベル
				</span>
				<span
					className="text-ui"
					style={{ marginLeft: "auto", color: "var(--fg-0)", fontWeight: 600 }}
				>
					{label}
				</span>
			</div>
			<div style={{ position: "relative" }}>
				<input
					ref={sliderRef}
					type="range"
					min={0}
					max={max}
					step={1}
					value={position}
					disabled={max === 0}
					onChange={(e) => onChange(efforts[Number(e.target.value)])}
					data-testid="chat-effort-selector-slider"
					className="izanagi-slider"
					style={
						{
							"--slider-track-size": `${TRACK_SIZE}px`,
							"--slider-thumb-size": `${THUMB_SIZE}px`,
							"--slider-ratio": ratio,
						} as CSSProperties
					}
				/>
				{/* つまみの上に重ならないよう、粒子はつまみの左端までに収める。 */}
				<div
					className="izanagi-effort-particles"
					style={{
						top: (THUMB_SIZE - TRACK_SIZE) / 2,
						height: TRACK_SIZE,
						width: `calc((100% - ${THUMB_SIZE}px) * ${ratio})`,
					}}
				>
					{PARTICLES.slice(0, Math.ceil(PARTICLE_COUNT * ratio)).map((particle) => (
						<span
							key={particle.id}
							style={{
								animationDuration: `${particle.duration}s`,
								animationDelay: `${particle.delay}s`,
							}}
						>
							<span
								style={{
									top: `${particle.top}%`,
									width: particle.size,
									height: particle.size,
									opacity: particle.opacity,
								}}
							/>
						</span>
					))}
				</div>
				{/* まだ選んでいない段階の点をトラックの中に重ねる。塗りの中は粒子が流れるので描かない。 */}
				<div
					style={{
						position: "absolute",
						inset: 0,
						marginInline: THUMB_SIZE / 2,
						pointerEvents: "none",
					}}
				>
					{efforts.map(
						(effort, i) =>
							i > position && (
								<span
									key={effort}
									style={{
										position: "absolute",
										left: `${(i / max) * 100}%`,
										top: "50%",
										width: DOT_SIZE,
										height: DOT_SIZE,
										transform: "translate(-50%, -50%)",
										borderRadius: "var(--radius-full)",
										background: "var(--fg-3)",
									}}
								/>
							),
					)}
				</div>
			</div>
			<div className="text-section" style={{ display: "flex", color: "var(--fg-3)" }}>
				<span>速さ優先</span>
				<span style={{ marginLeft: "auto" }}>精度優先</span>
			</div>
		</div>
	);
}
